import { createFileRoute } from "@tanstack/react-router";
import { ActivityComparisonChart } from "../../components/ActivityComparisonChart.tsx";
import { ElevationGainChart } from "../../components/ElevationGainChart.tsx";
import { GradeAdjustedPaceTable } from "../../components/GradeAdjustedPaceTable.tsx";
import { QueryStatePanel } from "../../components/QueryStatePanel.tsx";
import { SportVolumeChart } from "../../components/SportVolumeChart.tsx";
import { WalkingBiomechanicsChart } from "../../components/WalkingBiomechanicsChart.tsx";
import { HIKING_PACE_COPY } from "../../lib/hikingPaceCopy.ts";
import { selectedRangeQueryInput } from "../../lib/timeRange.ts";
import { useTrainingDays } from "../../lib/trainingDaysContext.ts";
import { TRAINING_SLOW_QUERY_OPTIONS } from "../../lib/trainingQueryOptions.ts";
import { trpc } from "../../lib/trpc.ts";

export const Route = createFileRoute("/training/hiking")({
  component: HikingTab,
});

function shouldShowQueryError(query: { error: unknown; data: unknown }): boolean {
  if (!query.error) return false;
  if (Array.isArray(query.data)) return query.data.length === 0;
  return !query.data;
}

function HikingTab() {
  const { days } = useTrainingDays();

  const gradeAdjustedPace = trpc.hiking.gradeAdjustedPace.useQuery(
    selectedRangeQueryInput(days),
    TRAINING_SLOW_QUERY_OPTIONS,
  );
  const elevation = trpc.hiking.elevationProfile.useQuery(
    selectedRangeQueryInput(days),
    TRAINING_SLOW_QUERY_OPTIONS,
  );
  const biomechanics = trpc.hiking.walkingBiomechanics.useQuery(
    selectedRangeQueryInput(days),
    TRAINING_SLOW_QUERY_OPTIONS,
  );
  const routeComparison = trpc.hiking.activityComparison.useQuery(
    selectedRangeQueryInput(days),
    TRAINING_SLOW_QUERY_OPTIONS,
  );

  return (
    <>
      <div className="card p-4">
        <SportVolumeChart days={days} sport="hiking" />
      </div>
      <Section title={HIKING_PACE_COPY.title}>
        {shouldShowQueryError(gradeAdjustedPace) ? (
          <QueryStatePanel error={gradeAdjustedPace.error} />
        ) : (
          <GradeAdjustedPaceTable
            data={gradeAdjustedPace.data ?? []}
            loading={gradeAdjustedPace.isLoading}
          />
        )}
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Elevation Gain">
          {shouldShowQueryError(elevation) ? (
            <QueryStatePanel error={elevation.error} />
          ) : (
            <ElevationGainChart data={elevation.data ?? []} loading={elevation.isLoading} />
          )}
        </Section>

        <Section title="Walking Biomechanics">
          {shouldShowQueryError(biomechanics) ? (
            <QueryStatePanel error={biomechanics.error} />
          ) : (
            <WalkingBiomechanicsChart
              data={biomechanics.data ?? []}
              loading={biomechanics.isLoading}
            />
          )}
        </Section>
      </div>

      <Section title="Route Comparison">
        {shouldShowQueryError(routeComparison) ? (
          <QueryStatePanel error={routeComparison.error} />
        ) : (
          <ActivityComparisonChart
            data={routeComparison.data ?? []}
            loading={routeComparison.isLoading}
          />
        )}
      </Section>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-sm font-medium text-muted uppercase tracking-wider">{title}</h2>
      </div>
      <div className="card p-4">{children}</div>
    </section>
  );
}
